import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Angry, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from '../components/toasts';
import { Button, ErrorText, Spinner } from '../components/ui';
import { errorMessage, get, post } from '../lib/api';
import { dateTimeOf } from '../lib/format';

interface Topic {
  name: string;
  description: string;
  count: number;
  attendanceIds: string[];
}

interface TopicReport {
  id: string;
  attendances: number;
  topics: Topic[];
  truncated: boolean;
  createdAt: string;
}

interface MoodReport {
  events: Array<{ id: string; at: string; attendanceId: string; status: string; reason: string | null; operator: string | null; contact: string | null; ticket: string | null }>;
  byOperator: Array<{ operatorId: string | null; operator: string; events: number; attendances: number }>;
}

/** Relatórios com IA: assuntos do período (sob demanda) e clientes que ficaram irritados (por operador). */
export function AiReportSections({ from, to, onOpen }: { from: string; to: string; onOpen: (attendanceId: string) => void }) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <TopicsSection from={from} to={to} onOpen={onOpen} />
      <MoodSection from={from} to={to} onOpen={onOpen} />
    </div>
  );
}

function TopicsSection({ from, to, onOpen }: { from: string; to: string; onOpen: (attendanceId: string) => void }) {
  const qc = useQueryClient();
  const key = ['insights', 'topics', from, to];
  const q = useQuery({ queryKey: key, queryFn: () => get<{ enabled: boolean; report: TopicReport | null }>(`/insights/topics?from=${from}&to=${to}`) });
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  if (q.data && !q.data.enabled && !q.data.report) return null;
  const report = q.data?.report ?? null;

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await post<TopicReport>('/insights/topics', { from, to });
      qc.setQueryData(key, { enabled: true, report: r });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-fg-soft uppercase">
          <Sparkles className="size-3.5 text-accent" /> Assuntos do período
        </h2>
        {q.data?.enabled && (
          <Button size="sm" variant={report ? 'ghost' : 'primary'} loading={generating} onClick={() => void generate()} title="A IA lê os resumos dos atendimentos finalizados e agrupa por assunto">
            {report ? 'Gerar de novo' : 'Gerar com IA'}
          </Button>
        )}
      </div>
      {q.isLoading && <Spinner />}
      <ErrorText error={q.error} />
      {!report && q.data?.enabled && <p className="rounded-lg border border-border bg-panel p-3 text-sm text-fg-soft">Agrupa os atendimentos finalizados no período pelos assuntos mais frequentes.</p>}
      {report && (
        <div className="rounded-lg border border-border bg-panel">
          {report.topics.map((t) => {
            const pct = Math.round((t.count / Math.max(1, report.attendances)) * 100);
            const expanded = open === t.name;
            return (
              <div key={t.name} className="border-b border-border px-3 py-2 last:border-b-0">
                <button className="flex w-full items-center gap-2 text-left" onClick={() => setOpen(expanded ? null : t.name)}>
                  {expanded ? <ChevronDown className="size-3.5 shrink-0" /> : <ChevronRight className="size-3.5 shrink-0" />}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.name}</span>
                  <span className="text-xs text-fg-soft tabular-nums">
                    {t.count} · {pct}%
                  </span>
                </button>
                <div className="mt-1 ml-5 h-1.5 overflow-hidden rounded bg-muted">
                  <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                </div>
                {expanded && (
                  <div className="mt-1.5 ml-5 text-xs">
                    {t.description && <p className="mb-1 text-fg-soft">{t.description}</p>}
                    <div className="flex flex-wrap gap-1">
                      {t.attendanceIds.slice(0, 30).map((id, n) => (
                        <button key={id} className="rounded bg-muted px-1.5 py-0.5 hover:bg-accent/15" onClick={() => onOpen(id)}>
                          #{n + 1}
                        </button>
                      ))}
                      {t.attendanceIds.length > 30 && <span className="text-fg-soft">+{t.attendanceIds.length - 30}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <p className="border-t border-border px-3 py-1.5 text-[11px] text-fg-soft">
            {report.attendances} atendimentos{report.truncated ? ' (os mais recentes do período)' : ''} · gerado em {dateTimeOf(report.createdAt)}
          </p>
        </div>
      )}
    </section>
  );
}

function MoodSection({ from, to, onOpen }: { from: string; to: string; onOpen: (attendanceId: string) => void }) {
  const q = useQuery({ queryKey: ['insights', 'mood', from, to], queryFn: () => get<MoodReport>(`/insights/mood-events?from=${from}&to=${to}`) });
  const [showAll, setShowAll] = useState(false);
  const d = q.data;
  if (!d || (!d.events.length && !q.isLoading)) return null;
  return (
    <section>
      <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-fg-soft uppercase">
        <Angry className="size-3.5 text-danger" /> Clientes irritados (IA)
      </h2>
      <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
        <thead className="bg-muted text-left text-xs text-fg-soft">
          <tr>
            <th className="px-3 py-2">Com quem estava</th>
            <th className="px-3 py-2 text-right" title="Vezes em que a IA viu o cliente ficar irritado">Ocorrências</th>
            <th className="px-3 py-2 text-right">Atendimentos</th>
          </tr>
        </thead>
        <tbody>
          {d.byOperator.map((o) => (
            <tr key={o.operatorId ?? 'fila'} className="border-t border-border">
              <td className="px-3 py-2">{o.operator}</td>
              <td className="px-3 py-2 text-right tabular-nums">{o.events}</td>
              <td className="px-3 py-2 text-right tabular-nums">{o.attendances}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 rounded-lg border border-border bg-panel">
        {(showAll ? d.events : d.events.slice(0, 5)).map((e) => (
          <button key={e.id} className="block w-full border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-muted" onClick={() => onOpen(e.attendanceId)}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium">
                {e.contact ?? 'Contato'}
                {e.ticket ? ` · ${e.ticket}` : ''}
              </span>
              <span className="whitespace-nowrap text-fg-soft">{dateTimeOf(e.at)}</span>
            </div>
            <div className="text-xs text-fg-soft">
              {e.operator ? `Com ${e.operator}` : 'Na fila'}
              {e.reason ? `: ${e.reason}` : ''}
            </div>
          </button>
        ))}
        {d.events.length > 5 && (
          <button className="w-full px-3 py-1.5 text-xs text-accent" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Mostrar menos' : `Ver todas (${d.events.length})`}
          </button>
        )}
      </div>
      <p className="mt-1 text-[11px] text-fg-soft">Pela análise da IA das mensagens do cliente: um indício para conferir a conversa, não uma avaliação do operador.</p>
    </section>
  );
}
